/**
 * @file src/config/timezones.js
 * @description The one timezone list every picker in this app uses.
 *
 * WHY THIS EXISTS
 * There used to be three different pickers over the same two columns: a
 * 25-entry array in the admin panel, a 12-entry array in Profile, and the full
 * IANA list in Locations. All three wrote valid IANA ids, so nothing
 * incompatible was ever STORED — the damage was on the way back IN.
 *
 * A native <select> seeded with a value it has no <option> for reports
 * value === "" and selectedIndex === -1. So a company on Africa/Tripoli opened
 * in a picker that didn't list it rendered blank, and an uncontrolled form then
 * SAVED that blank back — silently wiping a timezone nobody had touched.
 *
 * The fix is structural rather than cosmetic: offer every zone the backend
 * accepts (it validates against the full 418-entry IANA list), and additionally
 * fold any unrecognised stored value into the options so even a retired zone id
 * round-trips instead of disappearing.
 *
 * Grouped MENA-first because that's the market; the rest follow alphabetically.
 */

/** Zones we put at the top, in this order — our actual venues. */
const MENA = [
    'Asia/Riyadh', 'Asia/Kuwait', 'Asia/Qatar', 'Asia/Bahrain', 'Asia/Dubai', 'Asia/Muscat',
    'Asia/Amman', 'Asia/Beirut', 'Asia/Damascus', 'Asia/Baghdad', 'Asia/Hebron', 'Asia/Jerusalem',
    'Asia/Aden', 'Africa/Cairo', 'Africa/Tripoli', 'Africa/Tunis', 'Africa/Algiers',
    'Africa/Casablanca', 'Africa/Khartoum', 'Europe/Istanbul',
];

/** Fallback for browsers without Intl.supportedValuesOf (pre-2022). */
const FALLBACK = [
    ...MENA, 'Asia/Karachi', 'Asia/Kolkata', 'Europe/London', 'Europe/Paris',
    'Europe/Berlin', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'UTC',
];

/** Every zone this browser knows about (~418), or the curated fallback. */
export const allTimezones = (() => {
    try {
        if (typeof Intl.supportedValuesOf === 'function') {
            const all = Intl.supportedValuesOf('timeZone');
            if (Array.isArray(all) && all.length) return all;
        }
    } catch (e) { /* fall through */ }
    return FALLBACK;
})();

/** Current UTC offset in minutes, or null if the zone can't be parsed. */
export const tzOffsetMinutes = (zone) => {
    if (!zone) return null;
    try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
            .formatToParts(new Date());
        const label = parts.find((p) => p.type === 'timeZoneName')?.value || '';
        const m = label.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
        if (!m) return label === 'GMT' ? 0 : null;
        return (m[1] === '-' ? -1 : 1) * (parseInt(m[2], 10) * 60 + parseInt(m[3] || '0', 10));
    } catch { return null; }
};

/** "Africa/Tripoli (UTC+2)" — zone plus its current offset. */
export const tzLabel = (zone) => {
    if (!zone) return '';
    const min = tzOffsetMinutes(zone);
    if (min === null) return zone;
    if (min === 0) return `${zone} (UTC)`;
    const sign = min < 0 ? '-' : '+';
    const abs = Math.abs(min);
    const h = Math.floor(abs / 60);
    const mm = abs % 60;
    return `${zone} (UTC${sign}${h}${mm ? `:${String(mm).padStart(2, '0')}` : ''})`;
};

/**
 * Options grouped for a picker.
 *
 * @param {string|null} current  the currently stored value. If it isn't in the
 *   known list (retired id, hand-edited row), it gets its own "Current setting"
 *   group at the top so the picker can always display what is actually saved.
 * @returns {Array<{label:string, items:Array<{label:string,value:string}>}>}
 */
export const timezoneGroups = (current = null) => {
    const opt = (z) => ({ label: tzLabel(z), value: z });
    const groups = [];

    if (current && !allTimezones.includes(current)) {
        groups.push({ label: 'Current setting', items: [opt(current)] });
    }

    groups.push({ label: 'Middle East & North Africa', items: MENA.filter((z) => allTimezones.includes(z)).map(opt) });

    const rest = allTimezones.filter((z) => !MENA.includes(z)).sort();
    const byRegion = {};
    rest.forEach((z) => {
        const region = z.includes('/') ? z.split('/')[0] : 'Other';
        (byRegion[region] = byRegion[region] || []).push(z);
    });
    Object.keys(byRegion).sort().forEach((region) => {
        groups.push({ label: region, items: byRegion[region].map(opt) });
    });

    return groups;
};

/** Best-effort country name -> IANA zone. Mirrors backend General::countryToTimezone. */
const COUNTRY_TZ = {
    'saudi arabia': 'Asia/Riyadh', ksa: 'Asia/Riyadh', kuwait: 'Asia/Kuwait',
    qatar: 'Asia/Qatar', bahrain: 'Asia/Bahrain', 'united arab emirates': 'Asia/Dubai',
    uae: 'Asia/Dubai', oman: 'Asia/Muscat', jordan: 'Asia/Amman', lebanon: 'Asia/Beirut',
    syria: 'Asia/Damascus', iraq: 'Asia/Baghdad', palestine: 'Asia/Hebron', yemen: 'Asia/Aden',
    egypt: 'Africa/Cairo', turkey: 'Europe/Istanbul', morocco: 'Africa/Casablanca',
    tunisia: 'Africa/Tunis', algeria: 'Africa/Algiers', libya: 'Africa/Tripoli',
    sudan: 'Africa/Khartoum', pakistan: 'Asia/Karachi', india: 'Asia/Kolkata',
    usa: 'America/New_York', 'united states': 'America/New_York', uk: 'Europe/London',
    'united kingdom': 'Europe/London', france: 'Europe/Paris', germany: 'Europe/Berlin',
};
export const countryToTimezone = (country) =>
    (country ? COUNTRY_TZ[String(country).trim().toLowerCase()] || null : null);
